import { sha256 } from "@noble/hashes/sha2";

/**
 * Deterministic 5x5 symmetric identicon (GitHub-style) derived from any
 * input bytes (typically an X25519 public key). Returns inline SVG markup
 * suitable for `{@html ...}`.
 *
 * The grid is symmetric about the vertical axis: cells in columns 0/1 mirror
 * to columns 4/3, so we only need 15 boolean bits (5 rows x 3 cols). The
 * first 15 bits of the SHA-256 digest fill them. Bytes 16..18 derive a hue
 * pair (cyan-leaning for variety while staying on-brand).
 *
 * Zero new deps: `@noble/hashes` is already used by the crypto layer.
 */
export function identiconSvg(
  seed: Uint8Array | null | undefined,
  size = 64,
): string {
  if (!seed || seed.length === 0) {
    return placeholderSvg(size);
  }

  const h = sha256(seed);
  // 5 rows x 3 unique columns = 15 bits.
  const cells: boolean[][] = [];
  for (let r = 0; r < 5; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < 3; c++) {
      const bit = (r * 3 + c) % 24; // safe within 256 bits
      const byteIdx = bit >> 3;
      const bitIdx = bit & 7;
      row.push(((h[byteIdx]! >> bitIdx) & 1) === 1);
    }
    // Mirror cols 0,1 to cols 4,3.
    row.push(row[1]!);
    row.push(row[0]!);
    cells.push(row);
  }

  // Hue rotation derived from later bytes — yields one of 4 brand pairings
  // so the avatar visibly responds to identity.reset() but never clashes.
  const palette = [
    ["#00d4ff", "#003a52"], // cyan
    ["#a78bfa", "#1f0e44"], // purple
    ["#ed3d5b", "#46060f"], // pink
    ["#22c55e", "#082916"], // green
  ];
  const idx = (h[16]! + h[17]! + h[18]!) % palette.length;
  const [fg, bg] = palette[idx]!;

  const cell = size / 5;
  let rects = "";
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      if (cells[r]![c]) {
        rects += `<rect x="${(c * cell).toFixed(2)}" y="${(r * cell).toFixed(2)}" width="${cell.toFixed(2)}" height="${cell.toFixed(2)}" fill="${fg}"/>`;
      }
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" shape-rendering="crispEdges" aria-hidden="true">` +
    `<rect width="${size}" height="${size}" fill="${bg}"/>` +
    rects +
    `</svg>`
  );
}

function placeholderSvg(size: number): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" aria-hidden="true">` +
    `<rect width="${size}" height="${size}" fill="rgba(255,255,255,0.06)"/>` +
    `<circle cx="${size / 2}" cy="${size / 2}" r="${size * 0.18}" fill="rgba(255,255,255,0.18)"/>` +
    `</svg>`
  );
}
